import React, { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { submitContact } from '../api';
import { Mail, Phone, MapPin, Clock, MessageSquare, Send } from 'lucide-react';

const Contact = () => {
  const { t, dir, lang } = useLang();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitContact(form);
      toast({ title: t('contact.success') });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast({ title: lang === 'ar' ? 'حدث خطأ، حاول مجدداً' : 'Error, please retry', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-28 pb-16 min-h-screen" dir={dir}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb] mb-4">— {t('nav.contact')} —</div>
          <h1 className="font-display text-5xl md:text-6xl text-[#0a0a0a]">{t('contact.title')}</h1>
          <p className="mt-4 text-[#111827]/60 max-w-xl mx-auto">{t('contact.subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          <div className="md:col-span-2 space-y-4">
            {[
              { icon: MapPin, label: t('contact.office'), value: 'Business Bay, Dubai, UAE' },
              { icon: Mail, label: 'Email', value: 'info@iaccounting.ae' },
              { icon: Phone, label: t('contact.phone'), value: '+971 55 109 6418' },
              { icon: Clock, label: lang === 'ar' ? 'ساعات العمل' : 'Business Hours', value: t('contact.hours') }
            ].map((c, i) => (
              <div key={i} className="glass-card rounded-xl p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#2563eb]/10 border border-[#2563eb]/25 flex items-center justify-center flex-shrink-0">
                  <c.icon className="w-4 h-4 text-[#3b82f6]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#2563eb]">{c.label}</div>
                  <div className="text-sm text-[#111827]/85 mt-1">{c.value}</div>
                </div>
              </div>
            ))}
            <a href="https://wa.me/971551096418" className="block glass-card rounded-xl p-6 group hover:-translate-y-0.5 transition-transform border-blue-500/20">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-400/30 flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-blue-400">WhatsApp</div>
                  <div className="text-sm text-[#111827]/85 mt-1">{lang === 'ar' ? 'تواصل فوري' : 'Chat with an advisor'}</div>
                </div>
              </div>
            </a>
          </div>

          <div className="md:col-span-3">
            <form onSubmit={submit} className="glass-card rounded-2xl p-8 md:p-10 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('contact.name')}</Label>
                  <Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827] focus-visible:ring-[#2563eb]/40" />
                </div>
                <div>
                  <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('contact.email')}</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('contact.phone')}</Label>
                  <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+971 ..." className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                </div>
                <div>
                  <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('contact.subject')}</Label>
                  <Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} className="h-12 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                </div>
              </div>
              <div>
                <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('contact.message')}</Label>
                <Textarea rows={5} required value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
              </div>
              <Button type="submit" disabled={sending} className="gold-button rounded-md h-12 px-8 w-full text-sm">
                {sending ? '...' : <><Send className="w-4 h-4 me-2" /> {t('contact.send')}</>}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
